import React, { memo } from 'react'
import { Box, Divider, Grid2, Stack } from '@mui/material'

import {
  buildMapLink,
  formatLocationInfo
} from '../../../util/component.helpers'
import Popup from '../../shared/popup/Popup'
import RedeemAsset from '../../redeem/RedeemAsset'
import customConfig from 'customConfig'
import { buildStatusLabelIcon } from '../../../util/nft.helpers'
import RichText from '../../shared/text/RichText'
import { Label } from '../../label'
import Text from '../../text/Text'
import RedeemProgress from '../../redeem/RedeemProgress'
import OutsideLink from '../../links/OutsideLink'
import { formatTicks } from '../../../util/string.helpers'
import RedeemTitle from '../../redeem/RedeemTitle'
import LearnMoreLink from '../../links/LearnMoreLink'
import BackButton from '../../shared/button/BackButton'
import SubmitButton from '../../shared/button/SubmitButton'
import { Scrollbar } from 'src/components/scrollbar'

const RedeemPointsConfirm = ({
  currentUserPoints,
  redeemOption,
  rewardsProfile: { loading },
  tokenProfile,
  onCancel,
  onSubmit,
  t,
  isDisabledPointsBurn,
  authId
}) => {
  const { name, value, website, locations, validUntil, metadata, isNft } =
    redeemOption
  const locationsLength = locations && locations.length
  const description = metadata?.description
  const exclusiveContent = metadata?.exclusiveContent
  const { label, icon } = buildStatusLabelIcon(exclusiveContent, 0)
  const formattedValue = formatTicks(value)
  const tokenSymbol = tokenProfile.symbol
  return (
    <Popup maxWidth='lg' onCancel={onCancel}>
      <Box sx={{ gap: 1.5, display: 'flex', mb: { xs: 1.5, md: 2 } }}>
        <BackButton onClick={onCancel} t={t} />
        <Box sx={{ flexGrow: 1 }} />
      </Box>
      <Grid2 container spacing={{ xs: 2, md: 4, lg: 6 }} pb={{ xs: 2, md: 3 }}>
        <Grid2 size={{ xs: 12, md: 5, lg: 7 }}>
          <Box sx={{ borderRadius: 2 }}>
            <RedeemAsset
              metadata={metadata}
              isNft={isNft}
              image={metadata?.coverImage}
            />
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 7, lg: 5 }}>
          <Stack spacing={2} sx={{ height: 1 }}>
            <RedeemTitle
              name={name}
              isNft={isNft}
              iconSize='xl'
              titleProps={{
                variant: 'h5',
                sx: { color: '' },
                noWrap: false
              }}
            />
            <Divider sx={{ borderStyle: 'dashed' }} />
            <Scrollbar sx={{ height: { xs: '100%', md: 332 } }}>
              <Stack spacing={2} alignItems='flex-start'>
                {description && (
                  <Text
                    variant='body2'
                    sx={{ color: 'text.secondary' }}
                    component='div'
                    label={
                      <RichText
                        content={description}
                        customCss={{
                          '.nft-custom-actions': { display: 'none' }
                        }}
                      />
                    }
                  />
                )}
                {validUntil && (
                  <Stack spacing={1}>
                    <Text variant='subtitle2' label={t('redeem.valid-until')} />
                    <Label color='info'>
                      {t(customConfig.dateFormatLabel, {
                        date: validUntil
                      })}
                    </Label>
                  </Stack>
                )}
                {locationsLength === 1 && (
                  <Stack spacing={1}>
                    <Text variant='subtitle2' label={t('redeem.location')} />
                    <LocationLink location={locations[0]} />
                  </Stack>
                )}
                {locationsLength > 1 && (
                  <Stack spacing={1}>
                    <Text variant='subtitle2' label={t('redeem.locations')} />
                    <Stack>
                      {locations.map((location) => (
                        <LocationLink key={location.id} location={location} />
                      ))}
                    </Stack>
                  </Stack>
                )}
                {website && (
                  <LearnMoreLink
                    label={t('shop.learn-more')}
                    to={customConfig.buildRedeemWebsiteUrl(website, authId)}
                  />
                )}
              </Stack>
            </Scrollbar>
            <Stack
              spacing={2}
              sx={{ position: { xs: 'static', md: 'sticky' } }}
            >
              <Divider sx={{ borderStyle: 'dashed', flexGrow: 1 }} />
              <Stack spacing={2} alignItems='flex-start'>
                {exclusiveContent && (
                  <Label endIcon={icon} color='primary'>
                    {t('common.exclusive-content')}:{' '}
                    {t(`nft.exclusive-content-status.${label}`)}
                  </Label>
                )}
                <RedeemProgress
                  currentUserPoints={currentUserPoints}
                  value={value}
                  tokenSymbol={tokenSymbol}
                  progressTextSx={{
                    typography: 'subtitle2'
                  }}
                  noTitle
                />
                <Box component='span' sx={{ typography: 'h6' }}>
                  {`${formattedValue} ${tokenSymbol}`}
                </Box>
              </Stack>
              <Divider sx={{ borderStyle: 'dashed' }} />
              <SubmitButton
                label={t('shop.buy-with-points', { symbol: tokenSymbol })}
                disabled={isDisabledPointsBurn}
                loading={loading}
                onClick={onSubmit}
                fullWidth
              />
            </Stack>
          </Stack>
        </Grid2>
      </Grid2>
    </Popup>
  )
}

export default memo(RedeemPointsConfirm)

const LocationLink = ({ location }) => {
  return (
    <OutsideLink
      label={formatLocationInfo(location)}
      to={buildMapLink(location)}
      color='text.primary'
      variant='body2'
    />
  )
}
